/**
 * HH-2: Abonnementen & contracten — shared vocabulary + display-only logic
 * for SubscriptionsScreen/SubscriptionFormScreen. Values match the backend
 * ENUM exactly (ETM_Subscriptions::VALID_CATEGORIES, plan §6's schema).
 *
 * The actual date/money computation (status, switch_from, weeks_until_end,
 * the monthly/yearly rollup, and the attention-needed sort order) all
 * happen server-side in ETM_Subscriptions_Logic and arrive already
 * computed on each row — this file only turns that computed data into
 * Dutch labels and groups an already-sorted list by category for display.
 * Same division of responsibility WP-2's isDeadlineAtRisk() documents for
 * itself: the one true computation lives in one place, not reimplemented
 * here.
 */
import { __ } from '../i18n.js';

export const SUBSCRIPTION_CATEGORIES = [
  { value: 'energie', icon: 'bolt', label: () => __('Energie', 'etm') },
  { value: 'internet_tv', icon: 'wifi', label: () => __('Internet & tv', 'etm') },
  { value: 'verzekering', icon: 'shield', label: () => __('Verzekering', 'etm') },
  { value: 'streaming', icon: 'subscriptions', label: () => __('Streaming', 'etm') },
  { value: 'telefoon', icon: 'call', label: () => __('Telefoon', 'etm') },
  { value: 'overig', icon: 'category', label: () => __('Overig', 'etm') },
];

export function subscriptionCategoryInfo(value) {
  return SUBSCRIPTION_CATEGORIES.find((c) => c.value === value) || SUBSCRIPTION_CATEGORIES[SUBSCRIPTION_CATEGORIES.length - 1];
}

export const BILLING_PERIODS = [
  { value: 'monthly', label: () => __('Per maand', 'etm'), suffix: () => __('/ maand', 'etm') },
  { value: 'yearly', label: () => __('Per jaar', 'etm'), suffix: () => __('/ jaar', 'etm') },
];

export function billingPeriodSuffix(value) {
  const found = BILLING_PERIODS.find((b) => b.value === value);
  return found ? found.suffix() : '';
}

/**
 * Status -> chip display. `null` for STATUS_NONE — plan §4.1's UX
 * paragraph literally lists "nothing" as one of the three chip states, so
 * the component renders no chip at all for it, same as EXPIRED getting no
 * loud color (see ETM_Subscriptions_Logic's own docblock for why EXPIRED
 * exists as a fourth, deliberately neutral state rather than reusing
 * "cancelable"/"opzegbaar", which would claim something false).
 *
 * @param {string} status 'none'|'upcoming'|'cancelable'|'expired'
 * @param {number|null} weeksUntilEnd Only meaningful for 'upcoming'.
 * @returns {{label: string, tone: 'amber'|'green'|'neutral'}|null}
 */
export function subscriptionStatusChip(status, weeksUntilEnd) {
  if (status === 'upcoming') {
    const weeks = weeksUntilEnd || 1;
    return {
      tone: 'amber',
      label: weeks === 1
        ? __('Loopt af over 1 week', 'etm')
        : __('Loopt af over %d weken', 'etm').replace('%d', weeks),
    };
  }
  if (status === 'cancelable') {
    return { tone: 'green', label: __('Opzegbaar', 'etm') };
  }
  if (status === 'expired') {
    return { tone: 'neutral', label: __('Verlopen', 'etm') };
  }
  return null;
}

/**
 * Groups an already-sorted subscription list (the server applies plan
 * §4.1's "attention-needed first, then by cost descending" order — see
 * ETM_Subscriptions::list_subscriptions()) into per-category sections,
 * preserving that relative order within each group. A stable partition,
 * deliberately not a re-sort: re-sorting here would silently undo the
 * server's ordering the moment this function's own logic drifted from it.
 *
 * Categories are always returned in SUBSCRIPTION_CATEGORIES' fixed order
 * (plan §6's own ENUM order) and only included when they have at least one
 * row among `subscriptions` — an empty household never renders five empty
 * section headers.
 */
export function groupSubscriptionsByCategory(subscriptions) {
  return SUBSCRIPTION_CATEGORIES.map((cat) => ({
    category: cat,
    items: subscriptions.filter((s) => s.category === cat.value),
  })).filter((group) => group.items.length > 0);
}
